/**
 * The scaffold model — coordinates, not a grid.
 *
 * Legs live at arbitrary (x, z) in metres; everything else references an
 * integer node level `p` on the absolute 500 mm grid (`nodeY(p)`), so a
 * standard on a roof and one on the ground share the same cup levels.
 */

import type { BracketKind, PlankWidth } from "../data/haki.ts";

export type { BracketKind };

/** Which ALP boards a deck is packed with: both widths for best coverage, or one of them. */
export type PlankFamily = "mix" | "170" | "230";

/** A point on the ground plane, metres. */
export type Point = Readonly<{ x: number; z: number }>;

export type BeamKind = "lb" | "erb";

/** Which side of a stair carries the handrail. */
/**
 * Handrail configuration on a stair, cycled by right-clicking it.
 *
 * HAKI pairs an outer handrail (HL) with the shorter inner one (HLI), so the
 * first two states are that pair on either hand; then the outer rail on both
 * sides; then none.
 */
export type StairRail = "hliLeft" | "hliRight" | "hlBoth" | "none";

/**
 * A stack of standard segments at one coordinate.
 * `gy` and `baseP` are solved during rendering, not authored.
 */
export type Leg = {
  x: number;
  z: number;
  /** Segment lengths in millimetres, bottom first. */
  segments: number[];
  /** Ground height under the leg, metres — resolved by the terrain raycast. */
  gy?: number;
  /** First node level the base jack reaches. */
  baseP?: number;
  /**
   * Set when the standard stands on a bracket's spigot rather than a base
   * jack: the node level of the bracket's cup group. There is no jack, the
   * bracket's stub counts toward the height, and `segments` may be empty —
   * the bare bracket end is still a standard as far as beams and guardrails
   * are concerned.
   */
  mountP?: number;
};

/**
 * A bracket (konsoll) cantilevering from a standard. Both kinds end in a stub
 * with a cup group at `p` and a spigot above it, where a mounted `Leg` stands.
 * The SKD's arm is a normal `Beam`; the SK carries its own.
 */
export type Bracket = {
  kind: BracketKind;
  /** The standard it hooks onto. */
  a: Point;
  /** The outer end — the stub the mounted leg stands on. */
  b: Point;
  p: number;
  /** Reach in millimetres. */
  len: number;
};

export type Beam = {
  a: Point;
  b: Point;
  p: number;
  kind: BeamKind;
  /** Module length in millimetres. */
  len: number;
};

export type Diagonal = {
  a: Point;
  pa: number;
  b: Point;
  pb: number;
};

/** Four corners in a fixed order: a→b is the plank direction, a→c the width. */
export type DeckCorners = readonly [Point, Point, Point, Point];

export type Deck = {
  c: DeckCorners;
  p: number;
  hatch: boolean;
  /** Which end of the bay the hatch opening sits at. */
  hatchEnd?: 0 | 1;
  /** Which side of the bay the 690 mm hatch strip runs along. */
  hatchSide?: 0 | 1;
  /** Plank slots the user has taken out. */
  removed: Set<number>;
  len: number;
  wid: number;
  /** Boards this deck is packed with; older decks without it follow the global setting. */
  fam?: PlankFamily;
};

/**
 * One ALP board laid on its own, hooked over two parallel edges at level `p`.
 * `a` and `b` are the centreline's ends, on the two edges' axes — no closed
 * bay is needed, just two beams (or bracket arms) an ALP length apart.
 */
export type Board = {
  a: Point;
  b: Point;
  p: number;
  wid: PlankWidth;
};

/** UTV ×2000: latched between a lower transom at `p` and an upper one at `p+4`. */
export type UtvStair = {
  utv: number;
  p: number;
  /** Bottom hook centre. */
  o: Point;
  dx: number;
  dz: number;
  rail?: StairRail;
};

/** UTV 500/1000/1500: hooked on one beam edge, descending outward at 44°. */
export type SmallStair = {
  small: number;
  a: Point;
  b: Point;
  p: number;
  dx: number;
  dz: number;
  rail?: StairRail;
};

export type Stair = UtvStair | SmallStair;

/**
 * A free-standing Ladder ST 2100 hooked over a beam edge, climbing the outside
 * of the scaffold. Unlike the hatch ladder it needs no deck — it is the way in
 * from the ground.
 */
export type Ladder = {
  a: Point;
  b: Point;
  p: number;
  /** Outward direction the foot leans. */
  dx: number;
  dz: number;
};

export type Rail = {
  a: Point;
  b: Point;
  p: number;
};

export type ToeBoard = {
  a: Point;
  b: Point;
  p: number;
  /** Unit vector pointing in toward the deck. */
  inX: number;
  inZ: number;
};

export type ScaffoldModel = {
  /** Keyed by `kXZ(x, z)` — a beam end "has a standard" iff a leg exists at exactly that key. */
  legs: Map<string, Leg>;
  beams: Beam[];
  diags: Diagonal[];
  decks: Deck[];
  stairs: Stair[];
  ladders: Ladder[];
  rails: Rail[];
  toes: ToeBoard[];
  brackets: Bracket[];
  boards: Board[];
};

/**
 * A reference to one part of the model.
 *
 * It lives here rather than in the engine because it names model parts, not
 * meshes — the engine reads it back off `Mesh.userData`, but the model layer
 * must not depend on the engine.
 */
export type PartRef =
  /** `top` marks the removable top segment of a stacked standard. */
  | Readonly<{ type: "leg"; key: string; top?: boolean }>
  | Readonly<{ type: "beam"; idx: number }>
  | Readonly<{ type: "diag"; idx: number }>
  | Readonly<{ type: "stair"; idx: number }>
  | Readonly<{ type: "ladder"; idx: number }>
  | Readonly<{ type: "rail"; idx: number }>
  | Readonly<{ type: "toe"; idx: number }>
  | Readonly<{ type: "bracket"; idx: number }>
  | Readonly<{ type: "board"; idx: number }>
  | Readonly<{ type: "deck"; idx: number }>
  | Readonly<{ type: "plank"; idx: number; slot: number; fam: number }>
  | Readonly<{ type: "freeEnd"; x: number; z: number; p: number }>;

export type PartType = PartRef["type"];

export function isUtvStair(stair: Stair): stair is UtvStair {
  return "utv" in stair;
}

export function isSmallStair(stair: Stair): stair is SmallStair {
  return "small" in stair;
}

/** Does this standard stand on a bracket rather than a base jack? */
export function isMounted(leg: Leg): boolean {
  return leg.mountP !== undefined;
}

export function createModel(): ScaffoldModel {
  return {
    legs: new Map(),
    beams: [],
    diags: [],
    decks: [],
    stairs: [],
    ladders: [],
    rails: [],
    toes: [],
    brackets: [],
    boards: [],
  };
}
